import React from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as EyeIcon } from 'assets/icons/eye.svg';
import { ReactComponent as PenIcon } from 'assets/icons/pen.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/trashBox.svg';
import clsx from 'clsx';
import IconButton from 'components/IconButton/IconButton';
import Tooltip from 'components/Tooltip/Tooltip';
import { ICON_BUTTON_SIZES, TOOLTIP_POSITIONS } from 'constants/interfaces';

import type IScenarioShowcaseHeader from './interfaces/IScenarioShowcaseHeader';

import styles from './scss/ScenarioShowcaseHeader.module.scss';

function ScenarioShowcaseHeader({
  title,
  isViewActive,
  isViewDisabled,
  isViewEditDisabled,
  startContent,
  hideActions,
  onView,
  onEdit,
  onDelete,
}: IScenarioShowcaseHeader): React.JSX.Element {
  const { t } = useTranslation();
  const handleNoop = () => {};

  const handleClickActions = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  return (
    <div className={styles.header}>
      {startContent ? (
        <div
          className={styles.startContent}
          onClick={handleClickActions}
          role="presentation"
        >
          {startContent}
        </div>
      ) : null}
      <span className={styles.title}>{title}</span>
      {!hideActions ? (
        <div
          className={styles.actions}
          onClick={handleClickActions}
          role="presentation"
        >
          <Tooltip
            hint={t('valueChainMap.view')}
            position={TOOLTIP_POSITIONS.BOTTOM}
            hintClassName={styles.actionTooltipHint}
          >
            <IconButton
              icon={<EyeIcon aria-hidden="true" />}
              onClick={onView || handleNoop}
              size={ICON_BUTTON_SIZES.small}
              ariaLabel={t('valueChainMap.scenarioViewAriaLabel')}
              className={clsx(styles.actionButton, {
                [styles.actionButtonActive]: isViewActive,
              })}
              disabled={isViewDisabled || !onView}
            />
          </Tooltip>
          <Tooltip
            hint={t('valueChainMap.edit')}
            position={TOOLTIP_POSITIONS.BOTTOM}
            hintClassName={styles.actionTooltipHint}
          >
            <IconButton
              icon={<PenIcon aria-hidden="true" />}
              onClick={onEdit || handleNoop}
              size={ICON_BUTTON_SIZES.small}
              ariaLabel={t('valueChainMap.scenarioEditAriaLabel')}
              className={styles.actionButton}
              disabled={isViewEditDisabled || !onEdit}
            />
          </Tooltip>
          <Tooltip
            hint={t('valueChainMap.delete')}
            position={TOOLTIP_POSITIONS.BOTTOM}
            hintClassName={clsx(styles.actionTooltipHint, styles.deleteActionTooltipHint)}
          >
            <IconButton
              icon={<TrashIcon aria-hidden="true" />}
              onClick={onDelete || handleNoop}
              size={ICON_BUTTON_SIZES.small}
              ariaLabel={t('valueChainMap.scenarioDeleteAriaLabel')}
              className={styles.actionButton}
              disabled={!onDelete}
            />
          </Tooltip>
        </div>
      ) : null}
    </div>
  );
}

export default ScenarioShowcaseHeader;
